import { createApp, reactive } from 'vue';
import { createPinia } from 'pinia';
import { configure } from 'vee-validate';
import PrimeVue from 'primevue/config';
import AntVue from 'ant-design-vue';
import { Amplify } from 'aws-amplify';
import { initRum } from '@/services/monitoring';
import 'jsoneditor/dist/jsoneditor.css'
import '@/assets/styles/index.css';

// @ts-ignore
import App from '@/App.vue';
import router from '@/router';
import aura from '@/assets/styles';
import configService from '@/services/configService'; // Adjust the path as necessary
import { ApplicationConfiguration } from '@/types';
import aplosApi from './services/aplosApi';
import { useMessageStore } from './store/messages';
import LoadingScreen from './shared/components/LoadingScreen.vue';
import serviceProvider from './serviceProvider';

const pinia = createPinia();

async function initApp() {

  const loaderState = reactive({
    status: 'loading' as 'loading' | 'error' | 'ready',
    message: '',
    version: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : '',
  });

  const loaderApp = createApp(LoadingScreen, { state: loaderState });
  // mount the loader into its own overlay container so the main app can mount to #app concurrently
  const loaderContainer = document.createElement('div');
  loaderContainer.id = 'loader-root';
  Object.assign(loaderContainer.style, {
    position: 'fixed',
    inset: '0',
    zIndex: '9999',
  });
  document.body.appendChild(loaderContainer);
  loaderApp.mount(loaderContainer);

  // Load configuration and initialize services
  try {
    await configService.loadApplicationConfig();

    const appConfig: ApplicationConfiguration | null = configService.getApplicationConfigData();
    const AwsConfig = appConfig?.idp;
    if (AwsConfig) {
      // console.debug('AWS Config', AwsConfig);
      Amplify.configure(AwsConfig);
    } else {
      console.error('Config is not available.');
    }

    // Fetch public messages non-blocking — failure should not prevent app load
    let publicMessages: any[] = [];
    try {
      const { data } = await aplosApi.getPublicOutMessages();
      publicMessages = data.messages;
    } catch (msgError) {
      console.warn('Failed to load public messages, continuing without them', msgError);
    }

    // Initialize the main app
    const app = createApp(App);

    // Global error handler
    app.config.errorHandler = (err, instance, info) => {
      console.error('Global error captured in initApp:', err, info, instance);
      // For example, you might trigger a reload:
      // window.location.reload();

      // Or show a user-friendly notification
      // notifyUser('An unexpected error occurred. Please refresh the page.');
    };

    initRum(appConfig);

    app.use(router);
    app.use(pinia);
    app.use(PrimeVue, {
      unstyled: true,
      pt: aura
    });
    app.use(AntVue);
    app.use(serviceProvider)

    const { messages } = useMessageStore();
    messages.logOutMessages = publicMessages;

    // keep loader visible while the router completes initial navigation / app mounts
    try {
      await router.isReady();
    } catch (e) {
      console.warn('router.isReady() failed or timed out', e);
    }

    // mount the main app (loader overlay stays visible)
    app.mount('#app');

    // once the main app is up, remove the loader
    loaderApp.unmount();
    loaderContainer.remove();

    configure({
      validateOnBlur: true,
      validateOnChange: false,
      validateOnInput: false,
      validateOnModelUpdate: false,
    });
  }
  catch (error: any) {
    loaderState.message = "There was an error loading the application.";
    loaderState.status = 'error';
    console.warn("Failed to load configuration")
    console.error(error);
  }


}

initApp();
